import React from 'react';
import { Box, Button, Chip, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import type { TrendSourceEvidenceRef } from '../types';
import { SECTION_HEADING_SX, TREND_COLORS } from '../trendTheme';

const GEOGRAPHY_LABELS: Record<TrendSourceEvidenceRef['geography'], string> = {
  sweden: 'Sverige',
  nordics: 'Norden',
  europe: 'Europa',
  usa_uk: 'USA/UK',
  global: 'Global',
  unknown: 'Okänd geografi',
};

const ACCESS_LABELS: Record<TrendSourceEvidenceRef['accessType'], string> = {
  fulltext: 'Fulltext',
  partial: 'Delvis text',
  snippet_only: 'Snippet',
  rss_only: 'RSS',
  paywall: 'Paywall',
  pdf_report: 'Rapport/PDF',
  discovery: 'Discovery',
  unknown: 'Okänd access',
};

function roleLabel(role: TrendSourceEvidenceRef['usageRole']): string {
  if (role === 'primary_evidence') return 'Primär evidens';
  if (role === 'inspiration') return 'Inspiration';
  if (role === 'collected_unused') return 'Insamlad, ej använd';
  if (role === 'rejected') return 'Avvisad';
  return 'Stödjande evidens';
}

function tierColor(tier: TrendSourceEvidenceRef['sourceTier']): 'success' | 'info' | 'warning' | 'default' {
  if (tier === 'A') return 'success';
  if (tier === 'B') return 'info';
  if (tier === 'C') return 'warning';
  return 'default';
}

type SourceEvidencePanelProps = {
  refs: TrendSourceEvidenceRef[];
  collectedButUnusedCount?: number;
  title?: string;
  dense?: boolean;
  dark?: boolean;
};

export default function SourceEvidencePanel({
  refs,
  collectedButUnusedCount = 0,
  title = 'Källunderlag',
  dense = false,
  dark = false,
}: SourceEvidencePanelProps) {
  const usedRefs = refs.filter((ref) => ref.usedForConclusion && ref.usageRole !== 'collected_unused');
  const unusedRefs = refs.filter((ref) => !ref.usedForConclusion || ref.usageRole === 'collected_unused');
  const visibleRefs = [...usedRefs, ...unusedRefs].slice(0, dense ? 4 : 10);
  const textColor = dark ? '#fff' : TREND_COLORS.textPrimary;
  const mutedColor = dark ? 'rgba(255,255,255,0.72)' : TREND_COLORS.textMuted;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: dense ? 1.75 : 2.5,
        borderColor: dark ? 'rgba(255,255,255,0.16)' : TREND_COLORS.divider,
        bgcolor: dark ? 'rgba(255,255,255,0.08)' : TREND_COLORS.cardBg,
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
          <Typography sx={{ ...SECTION_HEADING_SX, color: dark ? TREND_COLORS.gold : TREND_COLORS.navy }}>
            {title}
          </Typography>
          <Chip
            size="small"
            label={`${usedRefs.length} använda${collectedButUnusedCount || unusedRefs.length ? ` · ${collectedButUnusedCount || unusedRefs.length} ej använda` : ''}`}
            sx={{ bgcolor: dark ? 'rgba(255,255,255,0.12)' : '#fff', color: dark ? '#fff' : TREND_COLORS.textMuted }}
          />
        </Stack>

        {visibleRefs.length === 0 ? (
          <Typography variant="body2" sx={{ color: mutedColor }}>
            Inget klickbart källunderlag finns sparat för detta objekt ännu.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {visibleRefs.map((ref, index) => (
              <Box key={`${ref.sourceItemId || ref.originalUrl || ref.title}-${index}`}>
                {index > 0 && <Divider sx={{ borderColor: dark ? 'rgba(255,255,255,0.12)' : TREND_COLORS.divider, mb: 1 }} />}
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip size="small" color={tierColor(ref.sourceTier)} label={`Tier ${ref.sourceTier}`} sx={{ height: 22 }} />
                    <Chip size="small" label={GEOGRAPHY_LABELS[ref.geography]} sx={{ height: 22 }} />
                    <Chip size="small" label={ACCESS_LABELS[ref.accessType]} sx={{ height: 22 }} />
                    <Chip size="small" label={roleLabel(ref.usageRole)} sx={{ height: 22 }} />
                  </Stack>
                  <Typography variant="body2" sx={{ color: textColor, fontWeight: 700 }}>
                    {ref.title || ref.sourceName || 'Källa utan rubrik'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: mutedColor }}>
                    {ref.sourceName || 'Okänd källa'}
                    {ref.publishedAt ? ` · ${ref.publishedAt.slice(0, 10)}` : ''}
                    {ref.provenance === 'discovery' ? ' · Discovery' : ''}
                  </Typography>
                  {ref.originalUrl && (
                    <Button
                      component={Link}
                      href={ref.originalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      endIcon={<OpenInNewIcon fontSize="small" />}
                      sx={{ alignSelf: 'flex-start', px: 0, color: dark ? TREND_COLORS.gold : TREND_COLORS.navy }}
                    >
                      Öppna originalkälla
                    </Button>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
